/**
 * Shared building blocks + constants for the Analytics Appels page.
 *
 * This file was extracted from `AnalyticsCalls.jsx` (iter29 refactor,
 * 2026-07-22) with strict iso-behaviour — moved verbatim, no logic
 * change. If you tweak anything here, please re-run the 56-test
 * regression suite before shipping (backend + frontend).
 */
import { ArrowDown, ArrowUp, BarChart3, Filter, LineChart as LineChartIcon, Loader2, Phone, Target, Users } from "lucide-react";

// --------------------------------------------------------------- tabs
export const TABS = [
    { key: "overview", label: "Vue d'ensemble", icon: LineChartIcon, enabled: true },
    { key: "reachability", label: "Joignabilité & tentatives", icon: Phone, enabled: true },
    { key: "funnel", label: "Funnel & qualification", icon: Target, enabled: true },
    { key: "compare", label: "Comparatifs", icon: BarChart3, enabled: true },
    { key: "detail", label: "Détail candidats", icon: Users, enabled: true },
];

// --------------------------------------------------------------- status tones
export const STATUS_TONE = {
    answered_complete: { bg: "bg-emerald-500", label: "Répondu — complété", text: "text-emerald-700", soft: "bg-emerald-50" },
    answered_incomplete: { bg: "bg-emerald-300", label: "Répondu — incomplet", text: "text-emerald-600", soft: "bg-emerald-50/60" },
    answered_unavailable: { bg: "bg-amber-400", label: "Répondu — indisponible", text: "text-amber-700", soft: "bg-amber-50" },
    answered_refusal: { bg: "bg-rose-500", label: "Répondu — refus", text: "text-rose-700", soft: "bg-rose-50" },
    voicemail: { bg: "bg-indigo-400", label: "Messagerie", text: "text-indigo-700", soft: "bg-indigo-50" },
    no_answer: { bg: "bg-slate-400", label: "Pas de réponse", text: "text-slate-700", soft: "bg-slate-100" },
    busy: { bg: "bg-orange-400", label: "Occupé", text: "text-orange-700", soft: "bg-orange-50" },
    invalid_number: { bg: "bg-red-500", label: "Numéro invalide", text: "text-red-700", soft: "bg-red-50" },
    technical_failure: { bg: "bg-slate-500", label: "Échec technique", text: "text-slate-700", soft: "bg-slate-100" },
};

export const DAYS_FR = ["Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam.", "Dim."];

// --------------------------------------------------------------- helpers
export function defaultPeriod() {
    const now = new Date();
    const from = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
    return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

export function buildQueryParams(filters) {
    const p = {};
    if (filters.from) p.from = filters.from;
    if (filters.to) p.to = filters.to;
    if (filters.offer_id) p.offer_id = filters.offer_id;
    if (filters.recruiter_id) p.recruiter_id = filters.recruiter_id;
    if (filters.source) p.source = filters.source;
    return p;
}

// --------------------------------------------------------------- shared cards
export function KpiCard({ label, value, unit, tone = "orange", delta, icon: Icon, testid, breakdown }) {
    const toneClasses = {
        orange: "bg-shariti-orange-soft text-shariti-orange",
        emerald: "bg-emerald-50 text-emerald-600",
        indigo: "bg-indigo-50 text-indigo-600",
        slate: "bg-slate-100 text-slate-500",
    }[tone];
    return (
        <div
            data-testid={testid}
            className="bg-white rounded-2xl border-2 border-slate-100 p-4 hover:border-shariti-orange/40 hover:shadow-lg transition"
        >
            <div className="flex items-start justify-between mb-3">
                <div className={`h-9 w-9 rounded-xl inline-flex items-center justify-center ${toneClasses}`}>
                    {Icon && <Icon className="h-4.5 w-4.5" strokeWidth={2.4} />}
                </div>
                {typeof delta === "number" && (
                    <span
                        className={`inline-flex items-center gap-0.5 text-[10px] font-black uppercase tracking-widest rounded-full px-1.5 py-0.5 ${
                            delta >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                        }`}
                    >
                        {delta >= 0 ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />}
                        {Math.abs(delta).toFixed(1)}%
                    </span>
                )}
            </div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">
                {label}
            </div>
            <div className="text-3xl font-black text-shariti-black leading-none">
                {value}
                {unit && <span className="text-lg text-slate-400 ml-0.5">{unit}</span>}
            </div>
            {/* iter88 · CEO 2026-09-01 — Le pourcentage seul est mystérieux
                ("93.5% de quoi ?"). Le breakdown "13 / 31" en fait un chiffre
                actionnable. Affiché seulement si fourni par le parent. */}
            {breakdown && (
                <div className="text-[11px] font-bold text-slate-500 mt-1.5" data-testid={`${testid}-breakdown`}>
                    {breakdown}
                </div>
            )}
        </div>
    );
}

export function LoadingBlock({ label }) {
    return (
        <div className="rounded-2xl border-2 border-slate-100 bg-white py-12 text-center flex flex-col items-center gap-2" data-testid="loading-block">
            <Loader2 className="w-5 h-5 text-shariti-orange animate-spin" />
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">{label}</span>
        </div>
    );
}

export function EmptyBlock({ label, small = false }) {
    return (
        <div className={`rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 text-center ${small ? "py-6" : "py-12"}`} data-testid="empty-block">
            <div className="text-xs font-black uppercase tracking-widest text-slate-400">{label}</div>
        </div>
    );
}

export function SectionCard({ title, icon: Icon, children, testid }) {
    return (
        <div className="rounded-2xl border-2 border-slate-100 bg-white p-4 md:p-5" data-testid={testid}>
            <div className="flex items-center gap-2 mb-4">
                {Icon && <Icon className="w-4 h-4 text-shariti-orange" strokeWidth={2.4} />}
                <div className="text-[11px] font-black uppercase tracking-widest text-shariti-black">{title}</div>
            </div>
            {children}
        </div>
    );
}

// --------------------------------------------------------------- filters bar
export function FiltersBar({ filters, onChange, offers, recruiters, sources }) {
    return (
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-3 flex flex-wrap items-center gap-3">
            <Filter className="h-4 w-4 text-slate-400" />
            <div className="flex items-center gap-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Du</label>
                <input
                    type="date"
                    value={filters.from}
                    onChange={(e) => onChange({ ...filters, from: e.target.value })}
                    data-testid="analytics-filter-from"
                    className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold focus:border-shariti-orange focus:outline-none"
                />
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Au</label>
                <input
                    type="date"
                    value={filters.to}
                    onChange={(e) => onChange({ ...filters, to: e.target.value })}
                    data-testid="analytics-filter-to"
                    className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold focus:border-shariti-orange focus:outline-none"
                />
            </div>
            <select
                value={filters.offer_id || ""}
                onChange={(e) => onChange({ ...filters, offer_id: e.target.value || null })}
                data-testid="analytics-filter-offer"
                className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold focus:border-shariti-orange focus:outline-none min-w-[10rem]"
            >
                <option value="">Toutes les offres</option>
                {offers.map((o) => (
                    <option key={o.id} value={o.id}>{o.title}</option>
                ))}
            </select>
            <select
                value={filters.recruiter_id || ""}
                onChange={(e) => onChange({ ...filters, recruiter_id: e.target.value || null })}
                data-testid="analytics-filter-recruiter"
                className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold focus:border-shariti-orange focus:outline-none min-w-[10rem]"
            >
                <option value="">Tous les chargés de recrutement</option>
                {recruiters.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                ))}
            </select>
            <select
                value={filters.source || ""}
                onChange={(e) => onChange({ ...filters, source: e.target.value || null })}
                data-testid="analytics-filter-source"
                className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold focus:border-shariti-orange focus:outline-none min-w-[10rem]"
            >
                <option value="">Toutes les sources</option>
                {sources.map((s) => (
                    <option key={s} value={s}>{s}</option>
                ))}
            </select>
            <div className="ml-auto text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                Comparaison auto vs. période précédente
            </div>
        </div>
    );
}
